import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Plane, Bot, Landmark, Sparkles, Check, Rocket, ArrowUpRight, Compass, GraduationCap, TrendingUp, Clock } from 'lucide-react';
import { innovations } from '../mock';
import { RevealHeading } from './anim/SectionReveal';
import { useEnrolmentModal } from '../context/EnrolmentModalContext';

const kindIcons = {
  vr: Plane,
  ai: Bot,
  bank: Landmark,
};

const kindMeta = {
  vr: [
    { icon: Plane, label: 'Flight sims' },
    { icon: Compass, label: 'Geography' },
    { icon: Clock, label: 'Time travel' },
  ],
  ai: [
    { icon: GraduationCap, label: 'Any subject' },
    { icon: Bot, label: '1-on-1 AI' },
    { icon: Sparkles, label: 'Adaptive' },
  ],
  bank: [
    { icon: Landmark, label: 'Savings' },
    { icon: TrendingUp, label: 'Live stocks' },
    { icon: Rocket, label: 'Micro-loans' },
  ],
};

const InnovationRow = ({ item, i }) => {
  const Icon = kindIcons[item.kind] || Sparkles;
  const meta = kindMeta[item.kind] || [];
  const isEven = i % 2 === 0;

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const imgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.14, 1, 1.06]);
  const rotate = useTransform(scrollYProgress, [0, 1], [isEven ? -2 : 2, isEven ? 2 : -2]);
  const textX = useTransform(scrollYProgress, [0, 0.5, 1], [isEven ? 40 : -40, 0, isEven ? -18 : 18]);

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.9 }}
      className={`grid md:grid-cols-12 items-center gap-10 md:gap-16 ${isEven ? '' : 'md:[&>*:first-child]:order-2'}`}
    >
      {/* Image side */}
      <motion.div style={{ y, rotate }} className="md:col-span-7 relative">
        {/* Border frame */}
        <motion.div
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="absolute -inset-6 border pointer-events-none origin-top"
          style={{ borderColor: `${item.accent}55` }}
        />
        {/* Numeral watermark */}
        <div
          className={`absolute ${isEven ? '-right-3 -top-14' : '-left-3 -top-14'} font-serif-display italic text-[110px] md:text-[160px] font-black leading-none pointer-events-none select-none opacity-15`}
          style={{ color: item.accent }}
        >
          0{i + 1}
        </div>
        <div className="relative overflow-hidden shadow-2xl">
          <motion.img
            style={{ scale: imgScale }}
            src={item.image}
            alt={item.imageAlt}
            className="relative w-full aspect-[4/3] object-cover"
          />
          {/* Gradient overlay tinted by accent */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(150deg, transparent 40%, ${item.accent}22 100%)`,
            }}
          />
          {/* Shine sweep */}
          <motion.div
            initial={{ x: '-100%' }}
            whileInView={{ x: '100%' }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, delay: 0.3, ease: [0.65, 0, 0.35, 1] }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
          />
          {/* Corner ticks */}
          <span className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2" style={{ borderColor: item.accent }} />
          <span className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2" style={{ borderColor: item.accent }} />
          <span className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2" style={{ borderColor: item.accent }} />
          <span className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2" style={{ borderColor: item.accent }} />
        </div>

        {/* Meta chips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="absolute -bottom-5 left-4 right-4 flex flex-wrap justify-center md:justify-start gap-2"
        >
          {meta.map((m) => {
            const MI = m.icon;
            return (
              <div key={m.label} className="bg-white shadow-md px-3 py-2 flex items-center gap-2 border-l-2 text-[11px] font-sans-pro uppercase tracking-[0.2em] text-[#4a4238]" style={{ borderColor: item.accent }}>
                <MI size={12} style={{ color: item.accent }} /> {m.label}
              </div>
            );
          })}
        </motion.div>
      </motion.div>

      {/* Text side */}
      <motion.div style={{ x: textX }} className="md:col-span-5">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 grid place-items-center border"
            style={{ borderColor: item.accent, color: item.accent }}
          >
            <Icon size={20} />
          </div>
          <div className="font-sans-pro text-[11px] uppercase tracking-[0.4em] font-semibold" style={{ color: item.accent }}>
            {item.tag}
          </div>
        </div>
        <div className="mt-4 font-serif-display italic text-[15px] text-[#8f6417]">{item.kicker}</div>
        <RevealHeading
          text={item.title}
          className="mt-2 font-serif-display text-3xl md:text-5xl font-black text-[#1c1a17] leading-[1.08]"
        />
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.7 }}
          className="h-px w-16 my-6 origin-left"
          style={{ background: item.accent }}
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.35, duration: 0.7 }}
          className="text-[#4a4238] leading-[1.75] text-[15px] font-light"
        >
          {item.lede}
        </motion.p>

        <ul className="mt-6 space-y-2.5">
          {item.bullets.map((b, k) => (
            <motion.li
              key={k}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ delay: 0.5 + k * 0.08 }}
              className="flex items-start gap-3 text-[14px] text-[#4a4238]"
            >
              <span
                className="mt-1 w-4 h-4 grid place-items-center shrink-0 rounded-sm"
                style={{ background: `${item.accent}22`, color: item.accent }}
              >
                <Check size={11} strokeWidth={3} />
              </span>
              <span className="leading-relaxed">{b}</span>
            </motion.li>
          ))}
        </ul>
      </motion.div>
    </motion.article>
  );
};

const Innovations = () => {
  const { show } = useEnrolmentModal();
  return (
    <section id="innovations" className="relative py-24 md:py-32 bg-white overflow-hidden">
      {/* Ambient gold streak */}
      <div className="absolute inset-x-0 top-0 h-40 pointer-events-none" style={{
        background: 'linear-gradient(180deg, rgba(200,148,42,0.06), transparent)',
      }} />

      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="text-center mb-24 max-w-3xl mx-auto"
        >
          <div className="tag-label justify-center">Whiteplains British School Launches</div>
          <RevealHeading
            text="Three world-firsts inside one school"
            highlight="world-firsts"
            className="font-serif-display text-4xl md:text-6xl font-black mt-5 text-[#1c1a17] leading-[1.05]"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.35, duration: 0.7 }}
            className="mt-6 text-[#4a4238] text-lg font-light leading-relaxed"
          >
            Immersive VR learning. An AI teacher who never tires. A real bank that lets students
            save, invest and trade stocks. Whiteplains British Schools is quietly rewriting what
            world-class education looks like.
          </motion.p>
        </motion.div>

        <div className="space-y-28 md:space-y-40">
          {innovations.map((item, i) => (
            <InnovationRow key={item.tag} item={item} i={i} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-24 md:mt-32 border border-[#eadfc9] p-8 md:p-12 bg-[#fbf7f2] flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div>
            <div className="font-sans-pro text-[11px] uppercase tracking-[0.4em] text-[#8f6417] font-semibold">Experience it in person</div>
            <h3 className="mt-3 font-serif-display text-2xl md:text-3xl font-black text-[#1c1a17]">
              Book a private tour of the Whiteplains British School Innovation Wing
            </h3>
          </div>
          <button
            type="button"
            onClick={() => show({ message: 'I would like a private tour of the Whiteplains British School Innovation Wing (VR room, AI teacher and Microfinance Bank).' })}
            className="btn-primary"
          >
            Reserve your visit <ArrowUpRight size={16} />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Innovations;
